package dev.lingvo.site.pages.app.account

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.SessionDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.i18n.t
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import dev.lingvo.site.i18n.tf
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Where you are signed in.
 *
 * Every token here carries a `sid` claim that is checked against a database row on each request, so
 * the Revoke button below genuinely ends the session. A page like this without that check is worse
 * than no page: it lists devices, returns 204, and leaves the revoked token working for a month — a
 * false claim made to exactly the person who reads this page most carefully.
 *
 * **API tokens are not here.** They live under Settings → API tokens, because mixing the two teaches
 * people that revoking a session revokes a token, and the day that matters is the day a laptop is
 * stolen.
 */
@Page("/app/account/security")
@Layout(".components.layouts.AppLayout")
@Composable
fun AccountSecurityPage() {
    val scope = rememberCoroutineScope()
    var refreshKey by remember { mutableStateOf(0) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }

    var sent by remember { mutableStateOf(false) }

    // Revoking a session and sending a password link both bump this counter. As a *key* it
    // discards the list before the request is sent, so the reader's own action blanked the page and
    // destroyed the button they had just pressed — keyboard focus fell to the document. The reload
    // token re-runs against the same subject and keeps the list up while it does.
    // Keyed on `Session.requestIdentity`, which that property exists for: "request caches must
    // include this key so refreshed memberships/features and an earlier browser session can never
    // publish into a later authenticated snapshot". These are account-level reads with no slug to
    // key on, and `loadReloadable` deliberately keeps its value across a reload token — so the one
    // thing that must still blank it is the reader changing.
    val sessions = loadReloadable(Session.requestIdentity, reloadToken = refreshKey) { ApiClient.sessions() }
    val account = loadReloadable(Session.requestIdentity, reloadToken = refreshKey) { ApiClient.me() }
    val state = sessions.state
    val me = account.state

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.account.security.security", "Security"), t("app.account.security.your_password_and_the_devices_signed", "Your password, and the devices signed in as you."))

        // `live`: this appears only because an action the reader just took failed, and it appears
        // above the control they are still on. A warning that arrives silently is one a
        // keyboard reader never learns about, and they will press the button again.
        // The refresh failure beside the retained list, not instead of it. `loadReloadable` keeps a
        // failed re-read out of `state` on purpose — that is how the last good list stays on screen
        // — so discarding `refreshError` meant a revoke whose re-read failed silently restored the
        // pre-revoke list with the revoked device still in it and said nothing at all. On this page
        // the reader concludes the revocation failed.
        (message ?: sessions.refreshError?.readable() ?: account.refreshError?.readable())?.let {
            LAlert(
                t("app.account.security.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        // The way out of a loop three refusals used to leave people in.
        //
        // Changing your address, handing over a workspace and deleting one all re-prove ownership
        // with a password, and all three refuse a Google-only account with "set a password first".
        // Nothing signed in could set one — the only route was to sign out and use "Forgot
        // password" on an account that has never had one, which reads as an error rather than a
        // step. The reset endpoint has always worked for these accounts; it was simply unreachable.
        // Was `(me as? Async.Success)?.value?.user?.let { … }`, which rendered nothing at all
        // while `/me` was in flight and nothing at all if it failed — no skeleton, no message,
        // no retry. On the one card that is a Google-only account's only route to a password,
        // an absence that looks identical to "you don't need this" is the wrong silence.
        LAsyncContent(
            state = me,
            rows = 2,
            loadingLabel = t("app.account.security.loading_your_password_settings", "Loading your password settings"),
            subject = t("app.account.security.your_password_settings", "your password settings"),
            onRetry = { refreshKey++ },
        ) { account ->
            account?.user?.let { user ->
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.gap(Space.SM)) {
                    LSectionTitle(t("app.account.security.password", "Password"))
                    LMuted(
                        if (user.hasPassword) {
                            t("app.account.security.you_have_one_changing_it_signs", "You have one. Changing it signs out every other device and revokes your API tokens — a password change that leaves standing credentials alive is not a password change.")
                        } else {
                            t("app.account.security.this_account_signs_in_with_google", "This account signs in with Google and has no password. Changing your email address, handing over a workspace or deleting one each ask for one, so set it here before you need it. Google sign-in keeps working either way.")
                        },
                    )
                    if (sent) {
                        LAlert(
                            tf("app.account.security.check_email", "Check {email}", "email" to user.email),
                            t("app.account.security.the_link_lasts_two_hours_setting_2", "The link lasts two hours. Setting a password signs out your other devices " +
                                "and revokes your API tokens."),
                            variant = LAlertVariant.INFO,
                        )
                    } else {
                        LButton(
                            if (user.hasPassword) t("app.account.security.email_me_a_link_to_change", "Email me a link to change it") else t("app.account.security.email_me_a_link_to_set", "Email me a link to set one"),
                            {
                                scope.launch {
                                    busy = true
                                    // `settingsOperationResult`, not `runCatching`: all three
                                    // calls on this page suspend, and `runCatching` catches the
                                    // `CancellationException` a navigation throws through this
                                    // scope as though it were a failure — reporting "That didn't
                                    // work" for a revocation that in all likelihood succeeded.
                                    message = settingsOperationResult {
                                        ApiClient.requestPasswordReset(user.email)
                                    }.fold({ sent = true; null }, { it.message })
                                    busy = false
                                }
                            },
                            enabled = !busy,
                            loading = busy,
                            variant = LButtonVariant.SECONDARY,
                        )
                    }
                }
            }
            }
        }

        LAsyncContent(
            state = state,
            loadingLabel = t("app.account.security.loading_sessions", "Loading sessions"),
            subject = t("app.account.security.your_signed_in_devices", "your signed-in devices"),
            onRetry = { refreshKey++ },
            // The cards, the revoke-others row and the token note are siblings; wrapped they are
            // one child of the page column and stopped inheriting its spacing.
            gap = Space.XL,
        ) { sessions ->
            sessions.forEach { session ->
                SessionCard(session) {
                    scope.launch {
                        busy = true
                        message = settingsOperationResult {
                            ApiClient.revokeSession(session.id)
                        }.fold({ refreshKey++; null }, { it.message })
                        busy = false
                    }
                }
            }

            if (sessions.size > 1) {
                LButton(
                    t("app.account.security.sign_out_everywhere_else", "Sign out everywhere else"),
                    {
                        scope.launch {
                            busy = true
                            message = settingsOperationResult {
                                ApiClient.revokeOtherSessions()
                            }.fold({ refreshKey++; null }, { it.message })
                            busy = false
                        }
                    },
                    enabled = !busy,
                    loading = busy,
                    variant = LButtonVariant.SECONDARY,
                )
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.gap(Space.XS)) {
                    LSectionTitle(t("app.account.security.api_tokens_are_separate", "API tokens are separate"))
                    LMuted(
                        t("app.account.security.signing_out_a_device_does_not_2", "Signing out a device does not revoke an API token, and revoking a token " +
                            "does not sign anybody out. That is deliberate: a stolen laptop must " +
                            "not break CI, and rotating a CI credential must not sign your team out."),
                    )
                    Link(path = "/app/settings/tokens") { Text(t("app.account.security.manage_api_tokens", "Manage API tokens")) }
                }
            }
        }
    }
}

@Composable
private fun SessionCard(session: SessionDto, onRevoke: () -> Unit) {
    LCard(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth().gap(Space.MD), verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.flexGrow(1).gap(Space.XXS)) {
                Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                    // Self-reported by the client, so rendered as text and never as markup.
                    Span(Modifier.fontWeight(600).toAttrs()) { Text(session.label ?: "Unknown device") }
                    // Marked rather than hidden, so somebody can see which one they would end.
                    if (session.current) LBadge(t("app.account.security.this_device", "this device"), variant = LBadgeVariant.SUCCESS)
                }
                // `formatUtc`, which is this exact expression plus the guard it was missing: a
                // timestamp of an unexpected shape now comes back whole instead of being sliced
                // into nineteen characters of nonsense with " UTC" appended.
                LMuted(tf(
                    "app.account.security.last_seen",
                    "Last seen {when}",
                    "when" to formatUtc(session.lastSeenAt),
                ))
            }
            if (!session.current) {
                LButton(
                    t("app.account.security.sign_out", "Sign out"),
                    onRevoke,
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    // Every row's button said "Sign out" and nothing said which device it ends,
                    // on the one screen where picking the wrong row logs you out of the machine
                    // you are reading it on.
                    accessibleLabel = tf(
                        "app.account.security.sign_out_device",
                        "Sign out {device}",
                        "device" to (
                            session.label
                                ?: t("app.account.security.this_unknown_device", "this unknown device")
                            ),
                    ),
                )
            }
        }
    }
}

